import React, { useEffect, useState, useCallback, useRef } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./discounts.module.css";

const cloudinaryCloudName = "dk1nepgzk";
const uploadPreset = "drxz9h8c";

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
    { method: "POST", body: formData }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message || "Image upload failed");
  return data.secure_url;
}

const thumbStyle = {
  width: 96,
  height: 64,
  objectFit: "cover",
  borderRadius: 8,
  border: "1px solid var(--line)",
  background: "var(--surface-2)",
  flexShrink: 0,
};

const SiteImages = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const canManage = canManageProducts();
  const fileInputRef = useRef(null);
  const pendingSlot = useRef(null);

  const fetchSlots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/site-assets`);
      if (!response.ok) throw new Error("Could not load the image list.");
      setSlots(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const saveSlot = async (slot, imageUrl, message) => {
    const response = await fetch(`${API_URL}/site-assets/${slot.key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ imageUrl }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || "Could not save the image.");
    }
    flash(message);
    fetchSlots();
  };

  const pickReplacement = (slot) => {
    pendingSlot.current = slot;
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const slot = pendingSlot.current;
    e.target.value = "";
    if (!file || !slot) return;
    setError("");
    setBusyKey(slot.key);
    try {
      const url = await uploadToCloudinary(file);
      await saveSlot(slot, url, `${slot.label} updated — live on the site now.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey(null);
    }
  };

  const handleReset = async (slot) => {
    if (!window.confirm(`Reset "${slot.label}" to the built-in image?`)) return;
    setError("");
    setBusyKey(slot.key);
    try {
      await saveSlot(slot, "", `${slot.label} reset to the built-in image.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Site Images</h1>
          <p className={styles.subtitle}>
            The hero, banners and tiles across the storefront. Upload a
            replacement and it goes live immediately; reset any slot to go back
            to the original design.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        {/* one hidden file input shared by all Replace buttons */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChosen}
        />

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Image slots</h2>
          {loading ? (
            <p className={styles.loading}>Loading images…</p>
          ) : (
            <ul className={styles.list}>
              {slots.map((slot) => (
                <li key={slot.key} className={styles.row}>
                  {slot.imageUrl ? (
                    <img src={slot.imageUrl} alt={slot.label} style={thumbStyle} />
                  ) : (
                    <div
                      style={{
                        ...thumbStyle,
                        display: "grid",
                        placeItems: "center",
                        fontSize: "0.65rem",
                        color: "var(--muted)",
                        textAlign: "center",
                      }}
                    >
                      built-in
                    </div>
                  )}
                  <div className={styles.rowInfo}>
                    <span className={styles.code}>{slot.label}</span>
                    <span className={styles.rowLabel}>
                      {slot.imageUrl
                        ? `Custom image · updated ${new Date(slot.dateUpdated).toLocaleDateString()}`
                        : "Using the built-in design image"}
                    </span>
                  </div>
                  {canManage && (
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        disabled={busyKey === slot.key}
                        onClick={() => pickReplacement(slot)}
                      >
                        {busyKey === slot.key ? "Uploading…" : "Replace"}
                      </button>
                      {slot.imageUrl && (
                        <button
                          type="button"
                          className={styles.dangerBtn}
                          disabled={busyKey === slot.key}
                          onClick={() => handleReset(slot)}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default SiteImages;
